import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { format } from "date-fns";
import {
  Plus, Trash2, ArrowDownLeft, ArrowUpRight, CalendarDays, Loader2, Settings2, X,
} from "lucide-react";
import { toast } from "sonner";
import { formatMoney, isoToday, isoDate, OTHER_OUT_HEAD_ID } from "@/lib/money";

const IN_COLOR  = "hsl(var(--status-present))";
const OUT_COLOR = "hsl(var(--status-absent))";

function firstDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function DatePickerBtn({ value, onChange, testid, label, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid={testid} className="h-10 justify-start rounded-md gap-2 w-[180px]">
            <CalendarDays className="h-4 w-4" />
            <span className="font-mono-mini tabular-nums text-xs">{format(value, "MMM d, yyyy")}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-auto">
          <Calendar mode="single" selected={value} onSelect={(d) => { if (d) { onChange(d); setOpen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function PettyCashTransactionsPage() {
  const [startDate, setStartDate] = useState(firstDayOfMonth());
  const [endDate, setEndDate] = useState(new Date());
  const [kindFilter, setKindFilter] = useState("all");
  const [heads, setHeads] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    date: isoToday(), kind: "in", head_id: "", amount: "", note: "",
    voucher_no: "", custom_head: "",
  });
  const [adding, setAdding] = useState(false);

  // Heads dialog state
  const [headsOpen, setHeadsOpen] = useState(false);
  const [newHead, setNewHead] = useState({ name: "", kind: "out" });

  const loadHeads = async () => {
    try {
      const { data } = await api.get("/petty-cash/heads");
      setHeads(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadTxs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: isoDate(startDate),
        end_date: isoDate(endDate),
      });
      if (kindFilter !== "all") params.set("kind", kindFilter);
      const { data } = await api.get(`/petty-cash/transactions?${params.toString()}`);
      setTxs(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadHeads(); }, []);
  useEffect(() => { loadTxs(); /* eslint-disable-next-line */ }, [startDate, endDate, kindFilter]);

  const submitTx = async (e) => {
    e.preventDefault();
    if (!form.head_id) { toast.error("Pick a head"); return; }
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (form.kind === "out" && !form.voucher_no.trim()) {
      toast.error("Voucher number is required for cash-out");
      return;
    }
    if (form.head_id === OTHER_OUT_HEAD_ID && !form.custom_head.trim()) {
      toast.error("Please specify the expense");
      return;
    }
    setAdding(true);
    try {
      await api.post("/petty-cash/transactions", {
        date: form.date,
        kind: form.kind,
        head_id: form.head_id,
        amount: amt,
        note: form.note || null,
        voucher_no: form.kind === "out" ? form.voucher_no.trim() : null,
        custom_head: form.head_id === OTHER_OUT_HEAD_ID ? form.custom_head.trim() : null,
      });
      toast.success(`${form.kind === "in" ? "Cash in" : "Cash out"} recorded`);
      setForm({ date: isoToday(), kind: form.kind, head_id: "", amount: "", note: "", voucher_no: "", custom_head: "" });
      setAddOpen(false);
      loadTxs();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAdding(false); }
  };

  const deleteTx = async (id) => {
    try {
      await api.delete(`/petty-cash/transactions/${id}`);
      toast.success("Transaction removed");
      loadTxs();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addHead = async () => {
    if (!newHead.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/petty-cash/heads", { name: newHead.name.trim(), kind: newHead.kind });
      toast.success("Head added");
      setNewHead({ name: "", kind: newHead.kind });
      loadHeads();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteHead = async (id) => {
    try {
      await api.delete(`/petty-cash/heads/${id}`);
      toast.success("Head removed");
      loadHeads();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const availableHeads = useMemo(() => {
    const filtered = heads.filter((h) => h.kind === form.kind);
    // Push the "Other (specify)" head to the bottom for cash-out
    return [...filtered].sort((a, b) => {
      if (a.head_id === OTHER_OUT_HEAD_ID) return 1;
      if (b.head_id === OTHER_OUT_HEAD_ID) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [heads, form.kind]);

  const totals = useMemo(() => {
    const t = { in: 0, out: 0 };
    txs.forEach((x) => { t[x.kind] += x.amount; });
    t.net = t.in - t.out;
    return t;
  }, [txs]);

  return (
    <div className="p-8 lg:p-10" data-testid="pc-transactions-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mini text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Ledger</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Transactions</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Every rupee in and out of the drawer, grouped by head.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={headsOpen} onOpenChange={setHeadsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="h-10 btn-lift" data-testid="pc-manage-heads-btn">
                <Settings2 className="h-4 w-4 mr-2" /> Heads
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="pc-heads-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Manage heads</DialogTitle>
                <DialogDescription>Buckets you can spend against or receive under.</DialogDescription>
              </DialogHeader>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">New head</Label>
                  <Input
                    data-testid="pc-new-head-name"
                    value={newHead.name}
                    onChange={(e) => setNewHead({ ...newHead, name: e.target.value })}
                    placeholder="e.g. Courier"
                    className="mt-1.5 h-10"
                  />
                </div>
                <ToggleGroup
                  type="single"
                  value={newHead.kind}
                  onValueChange={(v) => { if (v) setNewHead({ ...newHead, kind: v }); }}
                  className="border border-border rounded-md overflow-hidden h-10"
                >
                  <ToggleGroupItem value="in" data-testid="pc-new-head-kind-in" className="h-10 px-3 text-xs data-[state=on]:bg-[hsl(var(--status-present))] data-[state=on]:text-white rounded-none border-0">IN</ToggleGroupItem>
                  <ToggleGroupItem value="out" data-testid="pc-new-head-kind-out" className="h-10 px-3 text-xs data-[state=on]:bg-[hsl(var(--status-absent))] data-[state=on]:text-white rounded-none border-0">OUT</ToggleGroupItem>
                </ToggleGroup>
                <Button onClick={addHead} data-testid="pc-add-head-btn" className="h-10 btn-lift">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="max-h-72 overflow-y-auto border border-border rounded-md divide-y divide-border">
                {heads.length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">No heads yet.</div>
                )}
                {heads.map((h) => (
                  <div key={h.head_id} className="flex items-center gap-3 px-3 py-2">
                    <span
                      className="text-[10px] font-mono-mini px-1.5 py-0.5 rounded-sm text-white"
                      style={{ background: h.kind === "in" ? IN_COLOR : OUT_COLOR }}
                    >
                      {h.kind.toUpperCase()}
                    </span>
                    <span className="text-sm flex-1 truncate">{h.name}</span>
                    {h.is_default && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">default</span>}
                    {h.head_id !== OTHER_OUT_HEAD_ID && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`pc-delete-head-${h.head_id}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{h.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This head will be removed. If it's already used in transactions, the delete will be blocked.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteHead(h.head_id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="h-10 btn-lift" data-testid="pc-add-tx-btn">
                <Plus className="h-4 w-4 mr-2" /> Add transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="pc-add-tx-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Add transaction</DialogTitle>
                <DialogDescription>Record cash coming in or going out of the drawer.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submitTx} className="space-y-4">
                <ToggleGroup
                  type="single"
                  value={form.kind}
                  onValueChange={(v) => { if (v) setForm({ ...form, kind: v, head_id: "" }); }}
                  className="grid grid-cols-2 border border-border rounded-md overflow-hidden"
                >
                  <ToggleGroupItem
                    value="in"
                    data-testid="pc-tx-kind-in"
                    className="h-11 rounded-none border-0 data-[state=on]:bg-[hsl(var(--status-present))] data-[state=on]:text-white"
                  >
                    <ArrowDownLeft className="h-4 w-4 mr-2" /> Cash in
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="out"
                    data-testid="pc-tx-kind-out"
                    className="h-11 rounded-none border-0 data-[state=on]:bg-[hsl(var(--status-absent))] data-[state=on]:text-white"
                  >
                    <ArrowUpRight className="h-4 w-4 mr-2" /> Cash out
                  </ToggleGroupItem>
                </ToggleGroup>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
                    <Input
                      type="date"
                      data-testid="pc-tx-date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      required
                      className="mt-1.5 h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount ({"\u20B9"})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      data-testid="pc-tx-amount"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      required
                      className="mt-1.5 h-10 tabular-nums"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Head</Label>
                  <Select value={form.head_id} onValueChange={(v) => setForm({ ...form, head_id: v })}>
                    <SelectTrigger data-testid="pc-tx-head" className="mt-1.5 h-10">
                      <SelectValue placeholder={availableHeads.length ? "Pick a head" : "Add a head first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableHeads.map((h) => (
                        <SelectItem key={h.head_id} value={h.head_id}>
                          {h.head_id === OTHER_OUT_HEAD_ID ? "Other (specify below)" : h.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Specify expense when Other is selected */}
                {form.head_id === OTHER_OUT_HEAD_ID && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Specify the expense <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      data-testid="pc-tx-custom-head"
                      value={form.custom_head}
                      onChange={(e) => setForm({ ...form, custom_head: e.target.value })}
                      className="mt-1.5 h-10"
                      placeholder="e.g. Courier for board files"
                      autoFocus
                    />
                    <p className="text-[10px] text-muted-foreground mt-1.5">
                      This description will be recorded as the expense head.
                    </p>
                  </div>
                )}

                {/* Voucher number — required for cash out only */}
                {form.kind === "out" && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Voucher number <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      data-testid="pc-tx-voucher"
                      value={form.voucher_no}
                      onChange={(e) => setForm({ ...form, voucher_no: e.target.value })}
                      className="mt-1.5 h-10 font-mono-mini"
                      placeholder="e.g. V-2026-0142"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note (optional)</Label>
                  <Textarea
                    data-testid="pc-tx-note"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="mt-1.5"
                    placeholder="Invoice number, purpose…"
                    rows={2}
                  />
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={adding} data-testid="pc-tx-submit" className="btn-lift">
                    {adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save transaction
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="mt-8 p-5 rounded-2xl border-border shadow-soft">
        <div className="flex flex-wrap items-end gap-4">
          <DatePickerBtn value={startDate} onChange={setStartDate} testid="pc-filter-start" label="From" />
          <DatePickerBtn value={endDate}   onChange={setEndDate}   testid="pc-filter-end"   label="To" />
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
            <ToggleGroup
              type="single"
              value={kindFilter}
              onValueChange={(v) => { if (v) setKindFilter(v); }}
              className="border border-border rounded-md overflow-hidden h-10"
            >
              <ToggleGroupItem value="all" data-testid="pc-filter-all" className="h-10 px-3 text-xs rounded-none border-0 data-[state=on]:bg-foreground data-[state=on]:text-background">All</ToggleGroupItem>
              <ToggleGroupItem value="in" data-testid="pc-filter-in" className="h-10 px-3 text-xs rounded-none border-0 data-[state=on]:bg-[hsl(var(--status-present))] data-[state=on]:text-white">In</ToggleGroupItem>
              <ToggleGroupItem value="out" data-testid="pc-filter-out" className="h-10 px-3 text-xs rounded-none border-0 data-[state=on]:bg-[hsl(var(--status-absent))] data-[state=on]:text-white">Out</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="flex-1" />
          <div className="flex flex-wrap gap-3">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">In</div>
              <div className="font-display text-xl font-bold tabular-nums" style={{ color: IN_COLOR }}>{formatMoney(totals.in)}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Out</div>
              <div className="font-display text-xl font-bold tabular-nums" style={{ color: OUT_COLOR }}>{formatMoney(totals.out)}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Net</div>
              <div
                className="font-display text-xl font-bold tabular-nums"
                style={{ color: totals.net < 0 ? OUT_COLOR : IN_COLOR }}
                data-testid="pc-net-total"
              >
                {formatMoney(totals.net)}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <div className="mt-6 border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60 hover:bg-secondary/60">
              <TableHead className="w-[110px]">Date</TableHead>
              <TableHead className="w-[70px]">Type</TableHead>
              <TableHead className="w-[120px]">Voucher #</TableHead>
              <TableHead>Head</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right w-[140px]">Amount</TableHead>
              <TableHead className="text-right w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
              </TableCell></TableRow>
            )}
            {!loading && txs.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-14 text-center">
                <div className="text-sm text-muted-foreground">No transactions in this range.</div>
              </TableCell></TableRow>
            )}
            {!loading && txs.map((t) => (
              <TableRow key={t.tx_id} data-testid={`pc-tx-row-${t.tx_id}`} className="hover:bg-black/[0.03] dark:hover:bg-white/[0.03]">
                <TableCell className="font-mono-mini tabular-nums text-xs">{t.date}</TableCell>
                <TableCell>
                  <span
                    className="text-[10px] font-mono-mini px-1.5 py-0.5 rounded-sm text-white"
                    style={{ background: t.kind === "in" ? IN_COLOR : OUT_COLOR }}
                  >
                    {t.kind === "in" ? "IN" : "OUT"}
                  </span>
                </TableCell>
                <TableCell className="font-mono-mini text-xs text-muted-foreground">
                  {t.voucher_no || "—"}
                </TableCell>
                <TableCell className="text-sm">{t.head_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-md truncate">{t.note || "—"}</TableCell>
                <TableCell
                  className="text-right font-mono-mini tabular-nums text-sm font-medium"
                  style={{ color: t.kind === "in" ? IN_COLOR : OUT_COLOR }}
                >
                  {t.kind === "in" ? "+" : "−"} {formatMoney(t.amount)}
                </TableCell>
                <TableCell className="text-right">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`pc-delete-tx-${t.tx_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this transaction?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t.head_name} · {formatMoney(t.amount)} on {t.date}. This can't be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteTx(t.tx_id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
